import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Target, Flame, Trophy, Sparkles, Share2, Copy, Download, Award } from 'lucide-react';

const API = `${window.location.origin}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

/**
 * Motivation cards strip — sits below the stats and above the leaderboard.
 *
 * 4 cards in one section:
 *   1. Weekly Goal — bottles sold this week vs. owner-set target.
 *      A circular progress ring + "X more to go" copy. Hits 100% → confetti vibe.
 *   2. Streak — consecutive days with at least 1 sale. Flame icon scales with streak.
 *      Empty state is a soft nudge ("Start your streak today").
 *   3. Personal Records — best week ever (bottles + revenue), tier progress.
 *   4. Coach's Note — daily-rotating tip. Same tip all day for the same user;
 *      different users see different tips so they swap stories.
 *
 * One axios call. Refreshes on mount. Owner controls weekly_goal + tips in admin.
 */
export default function MotivationStrip({ profile, onWalletClick }) {
  const [motiv, setMotiv] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/ambassador/me/motivation`, { headers: authHeaders() })
      .then(r => setMotiv(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const copyLink = () => {
    if (!profile?.referral_url) return;
    navigator.clipboard.writeText(profile.referral_url);
    toast.success('Link copied — paste it sa post mo now!');
  };

  const shareToFB = () => {
    if (!profile?.referral_url) return;
    const u = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(profile.referral_url)}`;
    window.open(u, '_blank', 'noopener,noreferrer,width=600,height=500');
  };

  if (loading || !motiv) return null;

  const goalPct = motiv.weekly_goal?.percent || 0;
  const goalDone = motiv.weekly_goal?.current || 0;
  const goalTarget = motiv.weekly_goal?.target || 5;
  const goalRemaining = motiv.weekly_goal?.remaining ?? Math.max(0, goalTarget - goalDone);
  const streak = motiv.streak_days || 0;
  const bestWeek = motiv.best_week;
  const tip = motiv.daily_tip;
  const tierProg = motiv.tier_progress;

  return (
    <div className="mb-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="motivation-strip">
      {/* 1) Weekly Goal Ring */}
      <div className="bg-[#141414] border border-white/10 p-5 relative overflow-hidden" data-testid="motiv-weekly-goal">
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-sans flex items-center gap-1.5">
              <Target className="w-3 h-3" /> Weekly Goal
            </p>
            <p className="text-white/40 text-[11px] font-sans mt-0.5">Resets every Monday</p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <ProgressRing percent={goalPct} />
          <div className="flex-1 min-w-0">
            <p className="text-2xl text-white font-medium font-sans leading-none">
              {goalDone}<span className="text-white/30 text-lg"> / {goalTarget}</span>
            </p>
            <p className="text-white/40 text-[11px] font-sans mt-1">bottles</p>
            {goalRemaining > 0 ? (
              <p className="text-[#D4AF37] text-xs font-sans mt-2 font-medium">
                {goalRemaining} more to go
              </p>
            ) : (
              <p className="text-[#2E9B50] text-xs font-sans mt-2 font-medium">
                🎯 Goal smashed!
              </p>
            )}
          </div>
        </div>
      </div>

      {/* 2) Streak */}
      <div className="bg-[#141414] border border-white/10 p-5" data-testid="motiv-streak">
        <div className="flex items-start justify-between mb-3">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-sans flex items-center gap-1.5">
            <Flame className="w-3 h-3" /> Sales Streak
          </p>
        </div>
        {streak > 0 ? (
          <>
            <div className="flex items-baseline gap-1">
              <span
                className="text-4xl font-medium leading-none"
                style={{
                  color: streak >= 7 ? '#F97316' : streak >= 3 ? '#D4AF37' : '#FBBF24',
                  fontFamily: 'Cormorant Garamond, serif',
                }}
              >
                {streak}
              </span>
              <Flame className={`w-6 h-6 ${streak >= 7 ? 'text-orange-500' : 'text-[#D4AF37]'}`} />
            </div>
            <p className="text-white/60 text-xs font-sans mt-1">
              day{streak !== 1 ? 's' : ''} in a row
            </p>
            <p className="text-white/40 text-[11px] font-sans mt-2">
              {streak >= 30 ? "Legendary momentum 🏆" :
                streak >= 14 ? "Unstoppable 🔥" :
                streak >= 7 ? "On fire — keep it going!" :
                streak >= 3 ? "Streak building. Don't stop now." :
                "Sell 1 today to extend!"}
            </p>
          </>
        ) : (
          <div>
            <p className="text-3xl text-white/50 font-medium leading-none font-sans">0</p>
            <p className="text-white/40 text-xs font-sans mt-2">
              Make 1 sale today to start your streak.
            </p>
          </div>
        )}
      </div>

      {/* 3) Personal Record + Tier Progress */}
      <div className="bg-[#141414] border border-white/10 p-5" data-testid="motiv-records">
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-sans flex items-center gap-1.5 mb-3">
          <Trophy className="w-3 h-3" /> Personal Best
        </p>
        {bestWeek?.bottles > 0 ? (
          <>
            <p className="text-2xl text-white font-medium font-sans leading-none">
              {bestWeek.bottles}<span className="text-white/30 text-base"> btl</span>
            </p>
            <p className="text-[#D4AF37] text-xs font-sans mt-1">
              ₱{Number(bestWeek.revenue || 0).toLocaleString()} best week
            </p>
          </>
        ) : (
          <p className="text-white/50 text-sm font-sans">No record yet — your first week starts now.</p>
        )}

        {tierProg?.next_badge && tierProg?.bottles_to_next > 0 && (
          <div className="mt-3 pt-3 border-t border-white/5">
            <div className="flex items-center justify-between text-xs font-sans">
              <span className="text-white/50 flex items-center gap-1">
                <Award className="w-3 h-3 text-[#D4AF37]" /> Next: {tierProg.next_badge}
              </span>
              <span className="text-[#D4AF37] font-medium">{tierProg.bottles_to_next} btl away</span>
            </div>
            <div className="mt-1.5 h-1 bg-white/5 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#D4AF37] to-[#F3E5AB] transition-all"
                style={{ width: `${Math.max(2, Math.min(100, ((tierProg.all_time_bottles || 0) / (tierProg.next_target || 1)) * 100))}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* 4) Daily Coach's Note */}
      <div className="bg-gradient-to-br from-[#141414] via-[#1a1408] to-[#141414] border border-[#D4AF37]/20 p-5" data-testid="motiv-tip">
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-sans flex items-center gap-1.5 mb-3">
          <Sparkles className="w-3 h-3" /> Coach's Note
        </p>
        {tip ? (
          <p className="text-white/85 text-sm font-sans leading-relaxed italic">
            "{tip.text}"
          </p>
        ) : (
          <p className="text-white/40 text-sm font-sans">Stay sharp. Sell with conviction.</p>
        )}
        <p className="text-white/30 text-[10px] font-sans mt-3">
          New note tomorrow. Same time, same dashboard.
        </p>
      </div>

      {/* Quick actions (full-width) */}
      <div className="sm:col-span-2 lg:col-span-4 bg-[#0F0F0F] border border-white/5 p-3 flex flex-wrap gap-2 items-center" data-testid="motiv-quick-actions">
        <span className="text-white/40 text-[11px] uppercase tracking-wider font-sans pl-2">Quick Actions →</span>
        <button onClick={copyLink} data-testid="qa-copy-link"
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-white/10 text-white/70 hover:border-[#D4AF37]/40 hover:text-[#D4AF37] transition-colors">
          <Copy className="w-3 h-3" /> Copy Link
        </button>
        <button onClick={shareToFB} data-testid="qa-share-fb"
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-white/10 text-white/70 hover:border-[#D4AF37]/40 hover:text-[#D4AF37] transition-colors">
          <Share2 className="w-3 h-3" /> Share to FB
        </button>
        <button onClick={onWalletClick} data-testid="qa-withdraw"
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors ml-auto">
          <Download className="w-3 h-3" /> Request Withdrawal
        </button>
      </div>
    </div>
  );
}

function ProgressRing({ percent }) {
  const safe = Math.max(0, Math.min(100, percent));
  const r = 28;
  const c = 2 * Math.PI * r;
  const offset = c - (safe / 100) * c;
  return (
    <svg width="72" height="72" viewBox="0 0 72 72" className="-rotate-90 flex-shrink-0">
      <circle cx="36" cy="36" r={r} stroke="#1F1F1F" strokeWidth="6" fill="none" />
      <circle
        cx="36" cy="36" r={r}
        stroke={safe >= 100 ? '#2E9B50' : '#D4AF37'}
        strokeWidth="6"
        fill="none"
        strokeDasharray={c}
        strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 0.5s ease' }}
      />
      <text
        x="36" y="40"
        textAnchor="middle"
        transform="rotate(90 36 36)"
        className="fill-white text-xs font-sans font-medium"
        style={{ fontSize: '11px' }}
      >
        {safe}%
      </text>
    </svg>
  );
}
